import { DEFAULT_SETTINGS, type GameSettings, type SaveData, type Vec2 } from './types';

const STORAGE_KEY = 'tilt-arcade-save';

/**
 * Persistence layer for settings and best times.
 * Uses localStorage with JSON serialization.
 */
export class Persist {
  private data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Merge with defaults to handle schema changes
        return {
          settings: { ...DEFAULT_SETTINGS, ...parsed.settings },
          bestTimes: parsed.bestTimes ?? {},
          unlockedTables: parsed.unlockedTables ?? [],
          calibrationOffset: parsed.calibrationOffset ?? { x: 0, y: 0 },
          motionPermissionGranted: parsed.motionPermissionGranted ?? false,
        };
      }
    } catch (e) {
      console.warn('Failed to load save data:', e);
    }

    return {
      settings: { ...DEFAULT_SETTINGS },
      bestTimes: {},
      unlockedTables: [],
      calibrationOffset: { x: 0, y: 0 },
      motionPermissionGranted: false,
    };
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch (e) {
      console.warn('Failed to save data:', e);
    }
  }

  // Settings

  get settings(): GameSettings {
    return this.data.settings;
  }

  updateSettings(partial: Partial<GameSettings>): void {
    this.data.settings = { ...this.data.settings, ...partial };
    this.save();
  }

  // Best times

  getBestTime(tableId: string): number | null {
    return this.data.bestTimes[tableId] ?? null;
  }

  setBestTime(tableId: string, time: number): boolean {
    const current = this.data.bestTimes[tableId];
    if (current === undefined || time < current) {
      this.data.bestTimes[tableId] = time;
      this.save();
      return true; // New record
    }
    return false;
  }

  // Calibration

  get calibrationOffset(): Vec2 {
    return this.data.calibrationOffset;
  }

  setCalibrationOffset(offset: Vec2): void {
    this.data.calibrationOffset = { ...offset };
    this.save();
  }

  // Motion permission

  get motionPermissionGranted(): boolean {
    return this.data.motionPermissionGranted;
  }

  setMotionPermissionGranted(granted: boolean): void {
    this.data.motionPermissionGranted = granted;
    this.save();
  }

  // Debug

  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.data = this.load();
  }
}
